import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Trash2, Shield, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

export const DataControls = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-user-data', {
        body: { format: 'json' }
      });

      if (error) throw error;

      // Create and download the file
      const blob = new Blob([JSON.stringify(data, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nextwave-ai-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Data exported successfully",
        description: "Your data has been downloaded to your device.",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteData = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user-data');

      if (error) throw error;

      toast({
        title: "Data deletion initiated",
        description: "Your data deletion request has been processed. You will be logged out shortly.",
      });

      // Log out the user after successful deletion
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = 'https://app.nextwaveai.space';
      }, 2000);

    } catch (error) {
      console.error('Deletion error:', error);
      toast({
        title: "Deletion failed",
        description: "There was an error deleting your data. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Data Controls & Privacy Rights
        </CardTitle>
        <CardDescription>
          Exercise your data protection rights in compliance with GDPR and other privacy regulations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          {/* Export Data */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h4 className="font-medium">Download Your Data</h4>
              <p className="text-sm text-muted-foreground">
                Export all your personal data in JSON format, including uploaded files, settings, and account information.
              </p>
            </div>
            <Button
              onClick={handleExportData}
              disabled={isExporting}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isExporting ? "Exporting..." : "Export Data"}
            </Button>
          </div>

          {/* Delete Data */}
          <div className="flex items-center justify-between p-4 border rounded-lg border-destructive/20">
            <div className="flex-1">
              <h4 className="font-medium text-destructive">Delete All Data</h4>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Delete Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    Permanently Delete All Data?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      This will permanently delete your NextWave AI account and all associated data, including:
                    </p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>All uploaded files and datasets</li>
                      <li>Dashboard configurations and insights</li>
                      <li>CRM integrations and settings</li>
                      <li>Team memberships and shared resources</li>
                      <li>Account information and preferences</li>
                    </ul>
                    <p className="font-medium text-destructive">
                      This action cannot be undone. Are you absolutely sure?
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteData}
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? "Deleting..." : "Yes, Delete Everything"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">Your Privacy Rights</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Access:</strong> Request a copy of your personal data</li>
            <li>• <strong>Rectification:</strong> Correct inaccurate information in your account settings</li>
            <li>• <strong>Erasure:</strong> Delete your data using the controls above</li>
            <li>• <strong>Portability:</strong> Export your data in a machine-readable format</li>
            <li>• <strong>Restriction:</strong> Contact support to limit how we process your data</li>
            <li>• <strong>Objection:</strong> Opt out of certain types of data processing</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-3">
            For additional privacy rights or questions, contact <strong>support@nextwaveai.space</strong>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};